import ReactDOM from "react-dom";
import { createContext, useContext, useCallback, useState } from "react";
import { postWithDependencyInjection } from "./EndpointProvider";
import { useBackendLinksProvider } from "./BackendLinksProvider";
import { useTokenCookie } from "../Hooks/useTokenCookie";
import { useAuthBroadcast } from "../Hooks/useAuthBroadcast";

const WORKSPACE_NAME = window.env.REACT_APP_WORKSPACE_NAME
const AuthContext = createContext()

// Web Locks elect a single tab as the refresh leader. Followers skip the
// network call and pick up the new state from the leader's broadcast.
// Browsers without navigator.locks fall back to per-tab refresh.
const REFRESH_LEADER_LOCK = `iam-refresh-leader-${WORKSPACE_NAME}`

// Bound the refresh request so a hung connection can't hold the leader lock
// forever and starve every tab's next refresh.
const REFRESH_TIMEOUT_MS = 15_000

const timeoutSignal = (ms) =>
    typeof AbortSignal !== 'undefined' && AbortSignal.timeout
        ? AbortSignal.timeout(ms)
        : (() => {
            const ac = new AbortController()
            setTimeout(() => ac.abort(), ms)
            return ac.signal
        })()

// We never got a definitive answer from the server — release the lock and
// let the next periodic tick retry. Server-side rejections (which surface as
// strings from EndpointProvider) still fall through to logout(), preserving
// today's behavior for real auth failures.
const isTransientNetworkError = (err) =>
    err?.name === 'AbortError' || err instanceof TypeError

export const AuthProvider = ({ children }) => {
    const tokenName = `token_${WORKSPACE_NAME.toLowerCase()}`

    const { token, getToken, setToken, clearToken, reconcileFromCookie } = useTokenCookie(tokenName)
    const [permissions, setPermissions] = useState()
    const [currentUser, setCurrentUser] = useState()
    const [ibmCredentialWarnings, setIbmCredentialWarnings] = useState([])
    const { LINKS } = useBackendLinksProvider()

    const userIsLoggedIn = token !== undefined && token !== ""

    const [enableCheckActivity, setEnableCheckActivity] = useState(true)

    const localLogoutCleanup = useCallback(() => {
        clearToken()
        sessionStorage.removeItem('currentUploads')
        setIbmCredentialWarnings([])
        setPermissions(undefined)
        setCurrentUser(undefined)
    }, [clearToken])

    const { broadcast } = useAuthBroadcast({
        workspaceName: WORKSPACE_NAME,
        // Cross-tab login may have changed user identity; refresh
        // permissions and currentUser, not just the token.
        onRemoteLogin: () => refreshSession(),
        // Leader did the refresh; adopt its result without a second network call.
        onRemoteRefresh: ({ permissions, user }) => {
            reconcileFromCookie()
            if (permissions) setPermissions(permissions)
            if (user) setCurrentUser(user)
        },
        onRemoteLogout: localLogoutCleanup,
    })

    const logout = () => {
        // Tell the server to invalidate this token before we drop it
        // locally. Fire-and-forget — a network or server failure must
        // not block the user from getting out of the app.
        const currentToken = getToken()
        if (currentToken) {
            postWithDependencyInjection(currentToken, WORKSPACE_NAME, LINKS.ACCOUNT.LOGOUT)
                .catch(err => console.warn('Server-side logout failed:', err))
        }

        localLogoutCleanup()

        if (currentToken) broadcast('logged-out')
    }

    async function refreshSession() {
        const currentToken = getToken()
        if (!currentToken) {
            logout()
            return
        }

        const doRefresh = async () => {
            try {
                const res = await postWithDependencyInjection(
                    currentToken,
                    WORKSPACE_NAME,
                    LINKS.ACCOUNT.AUTH_SESSION_REFRESH,
                    undefined,
                    undefined,
                    timeoutSignal(REFRESH_TIMEOUT_MS),
                )

                const newToken = res?.rotated && res?.access_token ? res.access_token : currentToken
                setToken(newToken, res?.time_expires)

                if (res?.permissions) setPermissions(res.permissions)
                if (res?.user) setCurrentUser(res.user)

                broadcast('session-refreshed', {
                    permissions: res?.permissions,
                    user: res?.user,
                })
            } catch (e) {
                if (isTransientNetworkError(e)) {
                    console.warn('Session refresh deferred (transient):', e)
                    return
                }
                console.error('Session refresh failed:', e)
                logout()
            }
        }

        if (navigator.locks) {
            await navigator.locks.request(REFRESH_LEADER_LOCK, { ifAvailable: true }, async (lock) => {
                // Another tab is leader; trust its session-refreshed broadcast.
                if (!lock) { reconcileFromCookie(); return }
                await doRefresh()
            })
        } else {
            await doRefresh()
        }
    }

    async function login(email, password) {
        const data = await postWithDependencyInjection(undefined, WORKSPACE_NAME, LINKS.LOGIN.GET_TOKEN, { email, password })

        if (Object.values(data.permissions).every(element => element === false)) {
            throw new Error('You do not have permission to access CONNECT.')
        }

        let nextIbmCredentialWarnings = []
        try {
            const validationResult = await postWithDependencyInjection(
                data?.token,
                WORKSPACE_NAME,
                LINKS.LOGIN.CHECK_IBM_CREDENTIALS,
                { email }
            )

            nextIbmCredentialWarnings = Object.entries(validationResult ?? {})
                .filter(([, status]) => status !== 'Valid')
                .map(([command, status]) => ({ command, status }))

            if (nextIbmCredentialWarnings.length > 0) {
                console.warn('IBM credential validation warnings:', nextIbmCredentialWarnings)
            }
        } catch (error) {
            console.warn('IBM credential validation request failed:', error)
            nextIbmCredentialWarnings = [{ command: 'CHECK_IBM_CREDENTIALS', status: 'Validation request failed' }]
        }

        // If we don't batch the updates, it causes extra re-renders.
        // For some reason, React does not do this automatically..
        ReactDOM.unstable_batchedUpdates(() => {
            setToken(data.token)
            setPermissions(data.permissions)
            setCurrentUser(data.user)
            setIbmCredentialWarnings(nextIbmCredentialWarnings)
            setEnableCheckActivity(true)
        })

        broadcast('logged-in')
    }

    return (
        <AuthContext.Provider value={{ token, getToken, permissions, setPermissions, currentUser, ibmCredentialWarnings, userIsLoggedIn, refreshSession, logout, login, setToken, enableCheckActivity, setEnableCheckActivity }}>
            {children}
        </AuthContext.Provider>
    )
}

export const useAuthProvider = () => {
    return useContext(AuthContext)
}
